import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Gavel, AlertTriangle, Clock, CheckCircle2, ListChecks } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

// Field shape mirrors the PracAction Prisma model (apps/api/prisma/schema.prisma)
// and the GET /api/prac list response (apps/api/src/modules/prac/routes.ts).
interface PracAction {
  id: string;
  signalId: string | null;
  recommendationType: string;
  sourceUrl: string | null;
  issuedAt: string;
  dueDate: string | null;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled' | string;
  linkedVariationRef: string | null;
  linkedPsurId: string | null;
  linkedRmpId: string | null;
  notes: string | null;
}

export function PracActionsPage() {
  const [status, setStatus] = useState('');
  const [recType, setRecType] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const list = useQuery({
    queryKey: ['prac', { status, recType, overdueOnly }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('pageSize', '100');
      if (status) params.set('status', status);
      if (recType) params.set('recommendationType', recType);
      if (overdueOnly) params.set('overdue', 'true');
      try {
        return (await api.get(`/api/prac?${params.toString()}`)).data;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const items: PracAction[] = list.data?.items ?? [];
  const now = useMemo(() => Date.now(), []);

  const open = items.filter((a) => a.status === 'open').length;
  const inProgress = items.filter((a) => a.status === 'in_progress').length;
  const completed = items.filter((a) => a.status === 'completed').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">PRAC actions</h1>
        <p className="text-sm text-muted-foreground">
          PRAC recommendations tracked per GVP Module IX (Rev 1) Section IX.C.7. Each item links the
          public recommendation to the downstream variation, PSUR or RMP workflow.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={ListChecks} label="Total" value={list.data?.total ?? items.length} />
        <StatCard icon={AlertTriangle} label="Open" value={open} />
        <StatCard icon={Clock} label="In progress" value={inProgress} />
        <StatCard icon={CheckCircle2} label="Completed" value={completed} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Narrow by status, recommendation type, or show only those past their due date.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <Label htmlFor="prac-status">Status</Label>
              <select
                id="prac-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <Label htmlFor="prac-type">Recommendation type</Label>
              <select
                id="prac-type"
                value={recType}
                onChange={(e) => setRecType(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="ADDITIONAL_DATA_REQUEST">Additional data request</option>
                <option value="INCLUDE_IN_NEXT_PSUR">Include in next PSUR</option>
                <option value="VARIATION_PRODUCT_INFO">Variation (product info)</option>
                <option value="RMP_UPDATE">RMP update</option>
                <option value="ADDITIONAL_RM_MEASURE_OR_DHPC">Additional RM measure / DHPC</option>
                <option value="POST_AUTHORISATION_STUDY">Post-authorisation study</option>
                <option value="REFERRAL_PROCEDURE">Referral procedure</option>
                <option value="FURTHER_DATA_COLLECTION">Further data collection</option>
                <option value="EXPERT_CONSULTATION">Expert consultation</option>
                <option value="INSPECTION">Inspection</option>
                <option value="OTHER">Other</option>
                <option value="NO_ACTION">No action</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={overdueOnly}
                  onChange={(e) => setOverdueOnly(e.target.checked)}
                />
                Overdue only
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recommendations</CardTitle>
          <CardDescription>
            Source meeting reference, recommendation type, downstream link and current state.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Issued</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Linked signal</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!list.isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    No PRAC actions match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {items.map((a) => {
                const isOverdue =
                  a.dueDate &&
                  a.status !== 'completed' &&
                  a.status !== 'cancelled' &&
                  new Date(a.dueDate).getTime() < now;
                return (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono text-xs">{a.id.slice(0, 8)}</TableCell>
                    <TableCell className="text-xs">
                      {a.recommendationType.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell>
                      <StatusBadge s={a.status} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {a.issuedAt ? formatDate(a.issuedAt) : '-'}
                    </TableCell>
                    <TableCell
                      className={
                        isOverdue
                          ? 'text-xs font-medium text-destructive'
                          : 'text-xs text-muted-foreground'
                      }
                    >
                      {a.dueDate ? formatDate(a.dueDate) : '-'}
                      {isOverdue ? ' (overdue)' : ''}
                    </TableCell>
                    <TableCell className="text-xs">
                      {a.signalId ? (
                        <Link
                          to={`/signals/${a.signalId}`}
                          className="text-primary hover:underline"
                        >
                          {a.signalId.slice(0, 8)}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/prac/${a.id}`}>View</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ s }: { s: string }) {
  const v: Record<string, 'secondary' | 'info' | 'success' | 'destructive' | 'warning'> = {
    open: 'info',
    in_progress: 'warning',
    completed: 'success',
    cancelled: 'secondary',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s.replace(/_/g, ' ')}</Badge>;
}

// Re-export Gavel so it can be referenced in the sidebar without an extra import.
export { Gavel };
